/**
 * Category Seeding Service
 * 
 * Service for seeding system categories from CSV file
 * Following the service pattern from accounts feature
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import { Category, CreateCategoryInput, CategoryType } from '../types/category.types';
import { categoryService } from './category-service';

export interface CategorySeedingServiceResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

interface CSVCategoryRow {
  id: string;
  name: string;
  note: string;
  type: 'income' | 'expense';
  parent_id: string | null;
  gst_applicable: '0' | '1';
  deductible: '0' | '1';
  slug: string;
}

interface ParsedCategoryInput extends CreateCategoryInput {
  csvId: string;
  csvParentId?: string;
  slug?: string;
}

class CategorySeedingService {
  /**
   * Seed system categories from CSV file
   * Reads reference/Reference Import data/categories.csv
   * Creates 175 predefined categories with hierarchical structure
   */
  async seedSystemCategories(): Promise<CategorySeedingServiceResponse<Category[]>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Check if categories already exist for this user
      const existing = await categoryService.listCategories({ includeSystem: true });
      if (existing.data && existing.data.total > 0) {
        // Categories already seeded, return existing ones
        return {
          data: existing.data.categories.filter(cat => cat.isSystem)
        };
      }

      // Parse CSV file
      const csvData = await this.parseCategoriesCSV();
      
      // Create a map of CSV row ID to category ID for parent lookups
      const csvIdToCategoryId = new Map<string, string>();
      const createdCategories: Category[] = [];

      // First pass: Create parent categories (no parent_id in CSV)
      const parentCategories = csvData.filter(cat => !cat.csvParentId);
      
      for (const categoryInput of parentCategories) {
        const result = await supabase
          .from('categories')
          .insert({
            user_id: userId,
            parent_id: null,
            name: categoryInput.name,
            type: categoryInput.type,
            color: categoryInput.color || null,
            icon: categoryInput.icon || null,
            is_system: true,
            gst_applicable: categoryInput.gstApplicable || false,
            deductible: categoryInput.deductible || false,
            slug: categoryInput.slug || this.generateSlug(categoryInput.name),
            note: categoryInput.note || null
          })
          .select()
          .single();

        if (result.data) {
          const category: Category = {
            id: result.data.id,
            userId: result.data.user_id || '',
            parentId: result.data.parent_id || undefined,
            name: result.data.name,
            type: result.data.type as CategoryType,
            color: result.data.color || undefined,
            icon: result.data.icon || undefined,
            excludeFromSpending: result.data.exclude_from_spending ?? false,
            isSystem: result.data.is_system ?? false,
            gstApplicable: result.data.gst_applicable ?? false,
            deductible: result.data.deductible ?? false,
            slug: result.data.slug || undefined,
            note: result.data.note || undefined,
            createdAt: result.data.created_at || new Date().toISOString(),
            updatedAt: result.data.updated_at || new Date().toISOString()
          };
          createdCategories.push(category);
          // Map CSV row ID to created category ID
          if (categoryInput.csvId) {
            csvIdToCategoryId.set(categoryInput.csvId, category.id);
          }
        }
      }

      // Second pass: Create child categories (with parent_id in CSV)
      const childCategories = csvData.filter(cat => cat.csvParentId);
      
      // Group child categories by parent CSV ID
      const childrenByParent = new Map<string, typeof csvData>();
      for (const child of childCategories) {
        const parentCsvId = child.csvParentId!;
        if (!childrenByParent.has(parentCsvId)) {
          childrenByParent.set(parentCsvId, []);
        }
        childrenByParent.get(parentCsvId)!.push(child);
      }

      // Create children in batches by parent
      for (const [parentCsvId, children] of childrenByParent.entries()) {
        const parentId = csvIdToCategoryId.get(parentCsvId);
        if (!parentId) {
          console.warn(`Parent category not found for CSV ID: ${parentCsvId}`);
          continue;
        }

        for (const categoryInput of children) {
          const result = await supabase
            .from('categories')
            .insert({
              user_id: userId,
              parent_id: parentId,
              name: categoryInput.name,
              type: categoryInput.type,
              color: categoryInput.color || null,
              icon: categoryInput.icon || null,
              is_system: true,
              gst_applicable: categoryInput.gstApplicable || false,
              deductible: categoryInput.deductible || false,
              slug: categoryInput.slug || this.generateSlug(categoryInput.name),
              note: categoryInput.note || null
            })
            .select()
            .single();

          if (result.data) {
            const category: Category = {
              id: result.data.id,
              userId: result.data.user_id || '',
              parentId: result.data.parent_id || undefined,
              name: result.data.name,
              type: result.data.type as CategoryType,
              color: result.data.color || undefined,
              icon: result.data.icon || undefined,
              excludeFromSpending: result.data.exclude_from_spending ?? false,
              isSystem: result.data.is_system ?? false,
              gstApplicable: result.data.gst_applicable ?? false,
              deductible: result.data.deductible ?? false,
              slug: result.data.slug || undefined,
              note: result.data.note || undefined,
              createdAt: result.data.created_at || new Date().toISOString(),
              updatedAt: result.data.updated_at || new Date().toISOString()
            };
            createdCategories.push(category);
            // Map CSV row ID to created category ID
            if (categoryInput.csvId) {
              csvIdToCategoryId.set(categoryInput.csvId, category.id);
            }
          }
        }
      }

      return {
        data: createdCategories
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Parse CSV file and return category data
   * Reads reference/Reference Import data/categories.csv
   */
  private async parseCategoriesCSV(): Promise<ParsedCategoryInput[]> {
    try {
      // Fetch CSV file from public folder
      const response = await fetch('/categories.csv');
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV file: ${response.statusText}`);
      }

      const csvText = await response.text();
      const lines = csvText.split('\n').filter(line => line.trim().length > 0);
      
      // Skip header row
      const dataLines = lines.slice(1);
      
      const categories: ParsedCategoryInput[] = [];

      for (const line of dataLines) {
        // Parse CSV line (handles quoted fields)
        const parts = this.parseCSVLine(line);
        if (parts.length < 12) continue; // Skip invalid rows

        const row: CSVCategoryRow = {
          id: parts[0],
          name: parts[6],
          note: parts[7] || '',
          type: parts[8] as 'income' | 'expense',
          parent_id: parts[9] || null,
          gst_applicable: (parts[10] || '0') as '0' | '1',
          deductible: (parts[11] || '0') as '0' | '1',
          slug: parts[12] || this.generateSlug(parts[6])
        };

        // Skip if name is empty
        if (!row.name || row.name.trim().length === 0) continue;

        const categoryInput: ParsedCategoryInput = {
          csvId: row.id, // Store CSV row ID for parent mapping
          csvParentId: row.parent_id && row.parent_id !== '' ? row.parent_id : undefined,
          name: row.name.trim(),
          type: row.type === 'income' ? CategoryType.INCOME : CategoryType.EXPENSE,
          gstApplicable: row.gst_applicable === '1',
          deductible: row.deductible === '1',
          note: row.note.trim() || undefined,
          slug: row.slug || this.generateSlug(row.name),
          color: this.assignDefaultColor(row.type),
          icon: this.assignDefaultIcon(row.name)
        };

        categories.push(categoryInput);
      }

      return categories;
    } catch (error) {
      throw new Error(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Simple CSV line parser (handles quoted fields)
   */
  private parseCSVLine(line: string): string[] {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        parts.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    parts.push(current.trim());
    return parts;
  }

  /**
   * Generate URL-friendly slug from category name
   */
  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Assign default color based on category type
   */
  private assignDefaultColor(type: string): string {
    // Color palette for categories
    const colors = {
      income: ['#10B981', '#059669', '#047857'], // Green shades
      expense: ['#EF4444', '#DC2626', '#B91C1C', '#F59E0B', '#D97706', '#92400E'] // Red/Orange shades
    };

    const palette = type === 'income' ? colors.income : colors.expense;
    // Simple hash-based color assignment
    return palette[Math.abs(this.hashString(type)) % palette.length];
  }

  /**
   * Assign default icon based on category name
   */
  private assignDefaultIcon(name: string): string {
    // Map common category names to Lucide icons
    const iconMap: Record<string, string> = {
      'housing': 'home',
      'transportation': 'car',
      'utilities': 'zap',
      'groceries': 'shopping-cart',
      'dining': 'utensils',
      'healthcare': 'heart',
      'insurance': 'shield',
      'education': 'book',
      'entertainment': 'film',
      'personal care': 'user',
      'children': 'baby',
      'pets': 'dog',
      'gifts': 'gift',
      'savings': 'piggy-bank',
      'debt': 'credit-card',
      'taxes': 'file-text',
      'business': 'briefcase',
      'travel': 'plane',
      'income': 'dollar-sign',
      'transfers': 'arrow-left-right'
    };

    const nameLower = name.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (nameLower.includes(key)) {
        return icon;
      }
    }

    return 'folder'; // Default icon
  }

  /**
   * Simple string hash function
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash;
  }
}

export const categorySeedingService = new CategorySeedingService();

